import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {NGXLogger} from 'ngx-logger';
import {MatSnackBar} from '@angular/material/snack-bar';
import {LandEntitiesActions} from '../actions/land-entities.actions';
import {concatMap, exhaustMap, of, switchMap, take} from 'rxjs';
import {catchError, filter, map} from 'rxjs/operators';
import {InhaberEntitiesActions, mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {SentryActions} from '../actions/sentry.actions';
import {LandService} from '../../openapi/fakturierung-openapi';
import {LandExtended} from '../../interfaces/land-extended.interface';
import {Store} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {LandEntitiesSelectors} from '../selectors/land-entities.selectors';


@Injectable()
export class LandEntitiesEffects {
  private _store = inject(Store<AppState>);
  private _logger = inject(NGXLogger);
  private _landService = inject(LandService);
  private _actions$ = inject(Actions);
  private _snackbar = inject(MatSnackBar);

  readonly setCurrentInhaberIdFromURL$ = createEffect(
    () => this._actions$.pipe(
      ofType(InhaberEntitiesActions.setCurrentInhaberIdFromURL),
      map(action => action.currentInhaberId),
      filter((currentInhaberId): currentInhaberId is string => !!currentInhaberId),
      map(currentInhaberId => LandEntitiesActions.readLaender({betriebId: currentInhaberId})),
    ),
  );

  /**
   * Lädt die Länder vom Service.
   */
  readonly readLaender$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LandEntitiesActions.readLaender),
      exhaustMap(action =>
        this._store.select(LandEntitiesSelectors.laenderByBetrieb(action.betriebId)).pipe(
          take(1),
          map(existingEntities => ({existingEntities, betriebId: action.betriebId}))
        )
      ),
      filter(({existingEntities}) => existingEntities.length === 0),
      switchMap(({betriebId}) =>
        this._landService.readLaender(betriebId).pipe(
          map(landDtos => {
            this._logger.debug('read laender succeeded.');
            return LandEntitiesActions.readLaenderSuccess({
              betriebId,
              landDtos: landDtos as LandExtended[]
            });
          }),
          catchError(error =>
            of(error).pipe(
              mappedHttpErrorResponseOperator(error),
              concatMap(error => {
                this._logger.error('read laender failed. error: ', error);
                return [
                  LandEntitiesActions.readLaenderFailure({error}),
                  SentryActions.captureException({error, extras: {}}),
                ];
              })
            )
          )
        )
      )
    )
  );


  /**
   * Error-Handling für das Laden der Länder.
   */
  readonly readLaenderFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(LandEntitiesActions.readLaenderFailure),
      map(({error}) => {
        this._snackbar.open(
          'Fehler bei dem Laden der Länder-Informationen. Bitte probiere es später erneut.',
          undefined,
          {
            duration: 5000,
          });
      }),
    ),
    {dispatch: false},
  );

}
